import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Rocket, Users, Briefcase, FileText, GraduationCap,
  DollarSign, Globe2, ShieldCheck, LineChart, Sparkles,
  ArrowRight, Star, Mail, ChevronRight, Zap, Award,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png';
const PH_URL = 'https://www.producthunt.com/@i_amin';
const MAILTO = 'mailto:amin@he-direct.com?subject=H%20E%20Direct%20Consultants%20-%20Interested&body=Hi%20Amin%2C%0A%0AI%20saw%20H%20E%20Direct%20Consultants%20on%20Product%20Hunt%20and%20would%20love%20to%20chat.%0A%0A';

const Metric = ({ n, label, hint }) => (
  <div className="border border-white/15 rounded-2xl p-5 bg-white/5 backdrop-blur-sm">
    <div className="text-3xl md:text-4xl font-bold text-amber-400 tabular-nums">{n}</div>
    <div className="text-xs uppercase tracking-widest text-slate-300 mt-2">{label}</div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

const Feature = ({ icon: Icon, title, body, color }) => (
  <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-lg transition-shadow bg-white dark:bg-slate-900">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <h3 className="mt-4 font-semibold text-slate-900 dark:text-white text-lg">{title}</h3>
    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{body}</p>
  </div>
);

const Shot = ({ src, title, blurb }) => (
  <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-md">
    <div className="bg-slate-900 px-5 py-3 flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
      <span className="text-xs text-slate-400 ml-3">{title}</span>
    </div>
    <img src={src} alt={title} className="w-full block" loading="lazy" />
    {blurb && <p className="px-5 py-4 text-sm text-slate-600 dark:text-slate-300">{blurb}</p>}
  </div>
);

export default function LaunchPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = 'H E Direct Consultants — Study Abroad Platform for Pakistani Students';
    // Best-effort OG/description for previews when the URL is shared.
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'H E Direct Consultants — Study Abroad Platform for Pakistani Students. The CRM powering our study-abroad agency: leads, applications, agent networks, visa journeys, live P&L.');
    setMeta('og:title', 'H E Direct Consultants — Study Abroad Platform for Pakistani Students', 'property');
    setMeta('og:description', 'The CRM powering our study-abroad agency: leads, applications, agent networks, visa journeys, live P&L.', 'property');
    setMeta('og:image', LOGO, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:card', 'summary_large_image');
    axios.get(`${API}/api/public/traction`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const bigNum = (v) => (typeof v === 'number' ? v.toLocaleString() : '—');

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Sticky nav */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/85 dark:bg-slate-950/85 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="H E Direct Consultants" className="h-10 w-auto" />
            <div className="hidden sm:block">
              <div className="font-semibold text-sm leading-tight">H E Direct Consultants</div>
              <div className="text-[10px] text-slate-500 leading-tight">Study Abroad Platform for Pakistani Students</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <a href={PH_URL} target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#DA552F]/10 text-[#DA552F] hover:bg-[#DA552F]/20">
              <Star className="w-3 h-3" /> Product Hunt
            </a>
            <a href={MAILTO} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90">
              <Mail className="w-4 h-4" /> Book a demo
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900" />
        <div className="absolute inset-0 opacity-20 mix-blend-screen"
             style={{ backgroundImage: 'radial-gradient(600px 300px at 20% 10%, #f59e0b, transparent), radial-gradient(500px 300px at 90% 20%, #10b981, transparent), radial-gradient(400px 200px at 60% 90%, #3b82f6, transparent)' }} />
        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 text-white">
          <div className="flex items-center gap-2 mb-6">
            <a href={PH_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#DA552F] hover:bg-[#c14a25] text-white">
              <span className="w-4 h-4 rounded-full bg-white text-[#DA552F] flex items-center justify-center text-[10px] font-black">P</span>
              Live on Product Hunt
            </a>
            <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              <Sparkles className="w-3 h-3" /> Now in production
            </span>
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-4 font-semibold">
            H E Direct Consultants
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
            Study Abroad Platform for <span className="text-amber-400">Pakistani Students</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl">
            The full-stack CRM powering H E Direct — from lead capture in Peshawar and Islamabad,
            through UK / Europe university applications, visa journeys, agent networks, and monthly P&amp;L.
            Built inside a running agency, not a whiteboard.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={MAILTO} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300">
              <Mail className="w-4 h-4" /> Talk to the founder
            </a>
            <Link to="/investors" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/15">
              <LineChart className="w-4 h-4" /> Investor deck
            </Link>
            <a href={PH_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#DA552F] text-[#DA552F] hover:bg-[#DA552F]/10">
              <Rocket className="w-4 h-4" /> Support us on PH
            </a>
          </div>

          {/* Live traction */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric n={bigNum(stats?.leads_tracked)} label="Leads tracked" hint="Live from production" />
            <Metric n={bigNum(stats?.applications_processed)} label="Applications processed" />
            <Metric n={bigNum(stats?.active_agents)} label="Active agents" />
            <Metric n={bigNum(stats?.uk_courses_indexed)} label="UK courses indexed" />
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">
            What&rsquo;s inside
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
            Built by a Pakistani agency, for Pakistani students.
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            H E Direct Consultants runs offices in Peshawar and Islamabad. Every screen you see was born
            from real ops chasing UK visa deadlines with real students — not from a UX whiteboard.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Feature icon={Users} color="bg-teal-600" title="Lead management at scale"
            body="Import from Excel, dedupe by phone, auto-assign to counselors, chase unresponsive leads with WhatsApp + email in bulk." />
          <Feature icon={GraduationCap} color="bg-indigo-600" title="Full application journey"
            body="From inquiry → offer → CAS → refund → visa → enrolment. Twelve tracked stages with document management at each step." />
          <Feature icon={Briefcase} color="bg-amber-600" title="B2B + B2C agent network"
            body="Onboard agents with signed policies, track their students, split commissions, and enforce compliance policies automatically." />
          <Feature icon={DollarSign} color="bg-emerald-600" title="Live P&L calculator"
            body="Auto-sync consultancy fees from paid students into a monthly profit &amp; loss dashboard. See the business live, not quarterly." />
          <Feature icon={Globe2} color="bg-sky-600" title="Course + destination discovery"
            body="8,600+ UK courses indexed across 19 universities, mapped to intake dates, tuition fees, and English requirements." />
          <Feature icon={ShieldCheck} color="bg-slate-800" title="Role-based access"
            body="Super admins, HR, employees, agents, students — each with their own dashboard, permissions, and journey visibility rules." />
        </div>
      </section>

      {/* Screenshots */}
      <section className="bg-slate-50 dark:bg-slate-900/50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">
              Live product
            </div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold">Real screens. Real data.</h2>
            <p className="mt-3 text-slate-600 dark:text-slate-300">
              These are actual screenshots from H E Direct&rsquo;s production CRM — the same platform we&rsquo;re
              running today to process student applications for the September 2026 intake.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Shot src="/launch-shots/dashboard.jpeg" title="Admin Dashboard"
              blurb="Recruitment funnel, live KPIs, and one-click access to every ops surface." />
            <Shot src="/launch-shots/leads.jpeg" title="Leads Tab"
              blurb="Search, filter by staff/status/source, bulk upload from Excel, reassign to officers." />
            <Shot src="/launch-shots/journey.jpeg" title="Journey Timeline"
              blurb="Inquiry → Offer → Deposit → CAS → Refund → Visa → Enrolment. Every stage tracked." />
            <Shot src="/launch-shots/pnl.jpeg" title="P&amp;L Calculator"
              blurb="Auto-syncs paid consultancy fees. Live revenue vs cost. Monthly profitability at a glance." />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">
          Let&rsquo;s talk
        </div>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold">
          If you run a study-abroad agency in Pakistan —
        </h2>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          H E Direct Consultants is opening its CRM to a small group of partner agencies. Get in touch
          and we&rsquo;ll set up a walkthrough with your own leads and applications inside.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href={MAILTO} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold hover:opacity-90">
            <Mail className="w-4 h-4" /> amin@he-direct.com
          </a>
          <a href={PH_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#DA552F] text-[#DA552F] font-semibold hover:bg-[#DA552F]/10">
            <Award className="w-4 h-4" /> Upvote on Product Hunt
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
        <div className="mt-10 text-xs text-slate-500">
          Product Hunt · <a href={PH_URL} target="_blank" rel="noreferrer" className="underline hover:text-slate-700">@i_amin</a>
          <span className="mx-2">·</span>
          Investors: <Link to="/investors" className="underline hover:text-slate-700">/investors</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="H E Direct" className="h-8 w-auto" />
            <span>© {new Date().getFullYear()} Higher Education Direct</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-700 dark:hover:text-slate-300">Public site</Link>
            <a href={PH_URL} target="_blank" rel="noreferrer" className="hover:text-slate-700 dark:hover:text-slate-300">Product Hunt</a>
            <Link to="/investors" className="hover:text-slate-700 dark:hover:text-slate-300">Investors</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
